import { extend, override } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
import app from 'flarum/forum/app';
import icon from '../common/icon';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Post from 'flarum/forum/components/Post';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import PostControls from 'flarum/forum/utils/PostControls';
import Composer from 'flarum/forum/components/Composer';
import PostStream from 'flarum/forum/components/PostStream';
import PostStreamScrubber from 'flarum/forum/components/PostStreamScrubber';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import Stream from 'flarum/common/utils/Stream';
import { withFirstPostInclude, withTagsInclude } from './utils/listParams';
import { withCatalogSorts } from './utils/discussionSortMap';
import { readSettings } from '../common/settings';
import { createVoteAdapter } from '../common/voteAdapter';
import { getDepth, isHidden, isOriginalPost, getReplyTarget, getParentId, isDerivedParent, planSiblingFolding } from './utils/threadDepths';
import VoteRail from './components/VoteRail';
import CollapseToggle from './components/CollapseToggle';
import MoreReplies from './components/MoreReplies';
import NestedRepliesInlineReply from './components/NestedRepliesInlineReply';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { formatArabicPlural } from './utils/arabicPlural';
import { canOpenOpComposer, createOpComposerAutoOpen } from './utils/opComposerAutoOpen';
app.initializers.add('mtareq-nested-replies', () => {
  const settings = readSettings(app);

  // ---------------------------------------------------------------------------
  // Tree-aware scrubber
  // ---------------------------------------------------------------------------
  // Core's PostStreamScrubber is driven by the stream's number-ordered
  // index/count, while our DOM is tree-ordered (a reply sits under its parent).
  // Hand the scrubber a stream-shaped facade over the RENDERED order so its
  // handle, unread marker and click/drag navigation line up with the view.
  let treeScrollCache = null;
  function invalidateTreeScrollCache() {
    treeScrollCache = null;
  }

  // The item list is read from the DOM every time. Caching it meant the
  // scrubber kept the one item that existed during the loading paint — count
  // stayed "1 of 1" and the handle stayed full-height after the thread
  // rendered. Only the (more expensive) absolute tops are cached, keyed by the
  // number of items they were measured for.
  function treeItems() {
    const root = document.querySelector('.PostStream');
    return root ? Array.from(root.querySelectorAll('.PostStream-item')).filter(el => el.getAttribute('data-id') != null) : [];
  }
  function treeTops(items) {
    if (!treeScrollCache || treeScrollCache.count !== items.length) {
      treeScrollCache = {
        count: items.length,
        tops: items.map(el => el.getBoundingClientRect().top + window.pageYOffset)
      };
    }
    return treeScrollCache.tops;
  }

  // 0-based rendered index of the item at the top of the viewport.
  function renderedIndex() {
    const items = treeItems();
    if (!items.length) return 0;
    const tops = treeTops(items);
    const y = window.pageYOffset + 1;
    let lo = 0;
    let hi = items.length - 1;
    let ans = 0;
    while (lo <= hi) {
      const mid = lo + hi >> 1;
      if (tops[mid] <= y) {
        ans = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return ans;
  }
  function visibleCount() {
    const items = treeItems();
    if (!items.length) return 1;
    const tops = treeTops(items);
    const top = window.pageYOffset;
    const bottom = top + window.innerHeight;
    let n = 0;
    for (let i = 0; i < items.length; i++) {
      if (tops[i] < bottom && tops[i] + items[i].offsetHeight > top) n++;
    }
    return Math.max(1, n);
  }
  function scrollToRendered(i) {
    const items = treeItems();
    if (!items.length) return;
    const el = items[Math.max(0, Math.min(Math.floor(i), items.length - 1))];
    if (el && el.scrollIntoView) el.scrollIntoView({
      block: 'start'
    });
  }
  function makeTreeStream(real) {
    let dragIndex = null;
    return {
      discussion: real.discussion,
      loadPromise: Promise.resolve(),
      forceUpdateScrubber: false,
      // Keep it "unpaused": core's updateScrubberValues bails on scroll while paused.
      paused: false,
      description: '',
      disabled: () => false,
      count: () => Math.max(1, treeItems().length),
      // Core's scrubber is 1-based: the first post is "1 of N", and
      // updateScrubberValues() displays max(1, index). renderedIndex() is
      // 0-based, so translate at the boundary — without this the scrubber read
      // "0 of N" on every discussion and its height math was off by one.
      sanitizeIndex: i => Math.max(1, Math.min(i, treeItems().length)),
      get index() {
        return dragIndex != null ? dragIndex : renderedIndex() + 1;
      },
      set index(v) {
        dragIndex = v;
      },
      // The tree renders every post into the DOM, but only the ones crossing
      // the viewport are "visible"; core sizes the handle from this.
      get visible() {
        return visibleCount();
      },
      goToFirst() {
        dragIndex = null;
        scrollToRendered(0);
      },
      goToLast() {
        dragIndex = null;
        scrollToRendered(treeItems().length - 1);
      },
      goToIndex(i) {
        dragIndex = null;
        scrollToRendered(i - 1);
      }
    };
  }
  override('flarum/forum/components/PostStreamScrubber', 'oninit', function (original, vnode) {
    const stream = vnode.attrs && vnode.attrs.stream;
    if (stream && stream.discussion) {
      vnode.attrs.stream = makeTreeStream(stream);
    }
    return original(vnode);
  });

  // The scrubber setting is independent of the master switch.
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.classList.toggle('NestedRepliesHideScrubber', !settings.showScrubber);
  }
  if (!settings.enabled) return;

  // Scope the list-row vote gutter / negative margin / rail styles to this
  // class so the 34px column disappears when votes are turned off.
  document.documentElement.classList.toggle('NestedRepliesShowVotes', settings.showVotes);
  const votes = createVoteAdapter(app);

  // The list rail needs the discussion's original post (and its votes/userVote),
  // and every row needs its tags for the section pill. Requested client-side so
  // it works on both Flarum 1.8 and 2.0.
  override('flarum/forum/states/DiscussionListState', 'requestParams', function (original) {
    const params = withTagsInclude(original());
    if (!settings.showVotes) return params;
    return withFirstPostInclude(params);
  });

  // The list dropdown is built from this map's keys. We swap core's `top`
  // (replies) for our `replies` key and add `votes`; see discussionSortMap.js.
  override('flarum/forum/states/DiscussionListState', 'sortMap', function (original) {
    return withCatalogSorts(original());
  });
  const collapsed = new Set();
  const expandedGroups = new Set();
  const mounted = new Set();
  const lookup = id => app.store.getById('posts', String(id));

  // Recomputed on every stream render. `hidden` holds replies (and their
  // subtrees) folded behind a "Show more replies" control; `moreAfter` maps the
  // post that anchors each control to the groups it reveals.
  let foldPlan = {
    hidden: new Set(),
    moreAfter: new Map()
  };

  // Reply-card sorting. `oldest` uses Flarum's native stream; the other modes
  // fetch every page first so pagination can't leave posts out of the order.
  let sortMode = 'oldest';
  let allPosts = null;
  let loadingAll = false;
  let refreshing = false;
  let currentDiscussion = null;
  let pendingParentId = null;
  // Deep-link target (`/d/<id>/<near>`) captured before core resolves it, and a
  // one-shot flag so we re-apply the scroll exactly once per discussion.
  let deepLinkNear = null;
  let deepLinkApplied = false;
  // One-shot flag: re-arm core's scroll once the tree has replaced the stream.
  let scrollReapplied = false;
  // One-shot flag: pull the loading spinner into view when a discussion opens.
  let loadingScrollDone = false;

  // The open in-card reply form, and the draft it holds (shared so a target
  // switch can warn before discarding).
  let inlineReply = null;
  // One-shot guard for the auto-opened original-post composer. Module-level and
  // re-armed on every discussion change so navigating back and forth keeps
  // working (see utils/opComposerAutoOpen).
  const opComposerAutoOpen = createOpComposerAutoOpen();
  // A *native* reply composer (the sticky bar, a restored reply box) carries no
  // id we can see in hide(). Remember the discussion and
  // the newest post at the moment it opened, so hide() can tell a posted reply
  // from a cancel and focus the new one.
  let nativeReply = null;
  const inlineDraft = Stream('');
  // Whether the embedded composer is showing the rendered preview instead of
  // the editor (toggled by the composer's eye control).
  let composerPreview = false;

  // The post currently being edited inline (replaces the native composer edit).
  let editingPostId = null;
  const editDraft = Stream('');

  // The reply just posted, highlighted briefly so its author can spot it.
  const HIGHLIGHT_DURATION = 3000;
  let highlightedPostId = null;
  let highlightTimer = null;
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.classList.toggle('NestedRepliesHideMentionedBy', !settings.showRepliedIndicator);
    document.documentElement.style.setProperty('--nested-replies-like-color', settings.likeColor || '#ff4500');
    document.documentElement.style.setProperty('--nested-replies-highlight-rgb', hexToRgbTriplet(settings.highlightColor, '0, 200, 83'));
  }

  // Parse `#rrggbb` into an `r, g, b` triplet for use inside rgba(). Anything
  // else falls back to the default so the highlight never breaks.
  function hexToRgbTriplet(hex, fallback) {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) return fallback;
    const value = parseInt(match[1], 16);
    return `${value >> 16 & 255}, ${value >> 8 & 255}, ${value & 255}`;
  }

  // The discussion-level Reply button (the one above Follow) calls
  // DiscussionControls.replyAction. Route it to the in-card form on the
  // original post (a top-level reply) so it never opens the fixed composer.
  // Guests and non-replyable discussions keep core's behaviour.
  const originalReplyAction = DiscussionControls.replyAction;
  if (typeof originalReplyAction === 'function') {
    DiscussionControls.replyAction = function (...args) {
      const discussion = this;
      if (!app.session.user || !discussion || typeof discussion.canReply !== 'function' || !discussion.canReply()) {
        return originalReplyAction.apply(this, args);
      }
      const postIds = typeof discussion.postIds === 'function' ? discussion.postIds() : [];
      const op = postIds.length ? app.store.getById('posts', String(postIds[0])) : null;
      if (!op) return originalReplyAction.apply(this, args);
      openInlineReply(op);

      // Flarum 2's reply control treats the action's return value as a promise
      // (`DiscussionControls.replyAction(...).catch(...)`), so returning
      // undefined throws "Cannot read properties of undefined (reading
      // 'catch')". Resolve instead.
      return Promise.resolve();
    };
  }
  function removePostFromTree(postId) {
    const id = String(postId);
    if (allPosts && Array.isArray(allPosts)) {
      allPosts = allPosts.filter(post => String(post.id()) !== id);
    }
    collapsed.delete(id);
    expandedGroups.delete(id);
    forceRedraw();
  }

  // Flarum's native post actions open `confirm()`. Our DeleteConfirmModal is the
  // confirmation, so run the original core action with the native prompt
  // suppressed instead of copying its body (which would silently drift from core
  // in future releases).
  function runWithoutNativeConfirm(run) {
    const nativeConfirm = window.confirm;
    window.confirm = () => true;
    try {
      return run();
    } finally {
      window.confirm = nativeConfirm;
    }
  }

  // Replace the native confirm() of the hide/delete post actions with the custom
  // DeleteConfirmModal.
  if (PostControls) {
    override(PostControls, 'hideAction', function (original) {
      const post = this;
      app.modal.show(DeleteConfirmModal, {
        post,
        title: app.translator.trans('mtareq-nested-replies.forum.delete_post_title'),
        message: app.translator.trans('core.forum.post_controls.hide_confirmation'),
        confirmLabel: app.translator.trans('core.forum.post_controls.delete_button'),
        cancelLabel: app.translator.trans('mtareq-nested-replies.forum.reply_form_cancel'),
        onconfirm: () => {
          // Core keeps the post in the stream as a hidden placeholder and marks
          // it hidden optimistically; it must not be removed from the tree or
          // its replies would be re-rooted at the top level.
          Promise.resolve(runWithoutNativeConfirm(() => original())).then(() => forceRedraw(), () => forceRedraw());
        }
      });
    });
    override(PostControls, 'deleteAction', function (original, context) {
      const post = this;
      const postId = String(post.id());
      app.modal.show(DeleteConfirmModal, {
        post,
        title: app.translator.trans('mtareq-nested-replies.forum.delete_post_forever_title'),
        message: app.translator.trans('core.forum.post_controls.delete_confirmation'),
        confirmLabel: app.translator.trans('core.forum.post_controls.delete_forever_button'),
        cancelLabel: app.translator.trans('mtareq-nested-replies.forum.reply_form_cancel'),
        onconfirm: () => {
          // Core deletes the post and updates the discussion/store; drop it from
          // our cached tree as well so it vanishes without a page reload.
          Promise.resolve(runWithoutNativeConfirm(() => original(context))).then(() => removePostFromTree(postId), () => {});
        }
      });
    });
  }

  // Route core's "Edit" post action to the in-card form. Overriding the action
  // (rather than intercepting app.composer.load) avoids core's editAction
  // showing an empty native composer behind the inline form, and avoids hide()
  // discarding another composer's draft without the usual confirmation.
  if (PostControls && typeof PostControls.editAction === 'function') {
    override(PostControls, 'editAction', function (original) {
      const post = this;
      const canEdit = app.session.user && post && typeof post.canEdit === 'function' && post.canEdit();
      if (canEdit && (!post.contentType || post.contentType() === 'comment')) {
        editPost(post);
        return Promise.resolve();
      }
      return original.call(this);
    });
  }

  // Route the discussion's own destructive actions through the custom modal.
  // This covers both the list rows and the discussion's menu on the details
  // page (native confirm() is suppressed around core's original action).
  if (DiscussionControls) {
    override(DiscussionControls, 'hideAction', function (original) {
      app.modal.show(DeleteConfirmModal, {
        title: app.translator.trans('mtareq-nested-replies.forum.delete_discussion_title'),
        message: app.translator.trans('mtareq-nested-replies.forum.hide_discussion_confirmation'),
        confirmLabel: app.translator.trans('core.forum.discussion_controls.delete_button'),
        // Core's discussion hide() performs no native confirm(), so just call
        // the original action.
        onconfirm: () => original()
      });
    });
    override(DiscussionControls, 'deleteAction', function (original) {
      app.modal.show(DeleteConfirmModal, {
        title: app.translator.trans('mtareq-nested-replies.forum.delete_discussion_forever_title'),
        message: app.translator.trans('core.forum.discussion_controls.delete_confirmation'),
        confirmLabel: app.translator.trans('core.forum.discussion_controls.delete_forever_button'),
        onconfirm: () => runWithoutNativeConfirm(() => original())
      });
    });
  }

  // Keep the @ autocomplete to users only. Flarum's post mentionable offers the
  // discussion's posts as `@"name"#pN`, which is confusing; stop it suggesting
  // anything while leaving programmatic post mentions (quoting) intact.
  if (app.mentionFormats && typeof app.mentionFormats.mentionable === 'function') {
    const postMentionable = app.mentionFormats.mentionable('post');
    if (postMentionable) {
      postMentionable.initialResults = () => [];
      postMentionable.search = () => Promise.resolve([]);
    }
  }

  // Ensure every post has a Reply action. flarum/mentions supplies one when it
  // is enabled; otherwise we add our own so threading still works.
  extend('flarum/forum/components/CommentPost', 'actionItems', function (items) {
    if (items.has('reply')) return;
    const post = this.attrs.post;
    if (!post || post.isHidden()) return;
    if (post.isDeleted && post.isDeleted()) return;
    if (app.session.user && !post.discussion().canReply()) return;
    items.add('reply', m(Button, {
      className: 'Button Button--link',
      onclick: () => {
        pendingParentId = String(post.id());
        DiscussionControls.replyAction.call(post.discussion());
      }
    }, app.translator.trans('mtareq-nested-replies.forum.reply_link')), 0);
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('click', event => {
      const target = event.target;
      const replyItem = target && target.closest ? target.closest('.item-reply') : null;
      if (replyItem) {
        const item = replyItem.closest('.PostStream-item[data-id]');
        const id = item ? String(item.getAttribute('data-id')) : null;
        if (!id) return;
        const post = lookup(id);
        if (post && app.session.user && post.discussion().canReply()) {
          event.preventDefault();
          event.stopPropagation();
          openInlineReply(post);
          return;
        }

        // Guests, and users without reply permission, use Flarum's native flow.
        pendingParentId = id;
        const body = app.composer && app.composer.body;
        const open = app.composer && typeof app.composer.isVisible === 'function' && app.composer.isVisible() && body && body.attrs && body.attrs.discussion && !body.attrs.post;
        if (open) {
          body.attrs.replyToPostId = id;
          pendingParentId = null;
        }
        return;
      }
      if (target && target.closest && target.closest('.ReplyPlaceholder')) {
        pendingParentId = null;
      }
    }, true);
  }
  function patchReplyComposer(componentClass) {
    if (!componentClass || !componentClass.prototype) return;
    const proto = componentClass.prototype;
    if (proto.__nestedRepliesParentPatched) return;
    proto.__nestedRepliesParentPatched = true;
    const originalData = proto.data;
    proto.data = function () {
      const data = (originalData ? originalData.call(this) : {}) || {};
      const attrs = this.attrs || {};
      if (attrs.replyToPostId != null) data.replyToPostId = attrs.replyToPostId;
      return data;
    };

    // Core's preview control navigates to the full-page reply route. Inline,
    // toggle the rendered preview in place instead so the page does not jump.
    const originalJumpToPreview = proto.jumpToPreview;
    proto.jumpToPreview = function (...args) {
      if (!isInlineComposer()) {
        return originalJumpToPreview ? originalJumpToPreview.apply(this, args) : undefined;
      }
      composerPreview = !composerPreview;
      forceRedraw();
    };
  }
  if (app.composer && typeof app.composer.load === 'function') {
    override(app.composer, 'load', function (original, componentClass, attrs) {
      const result = original.call(this, componentClass, attrs);
      const apply = () => {
        const body = this.body;
        if (body && body.attrs && body.attrs.discussion && !body.attrs.post) {
          if (pendingParentId != null) {
            body.attrs.replyToPostId = pendingParentId;
            pendingParentId = null;
          }
          patchReplyComposer(body.componentClass);
        }
      };
      if (result && typeof result.then === 'function') {
        result.then(apply);
      } else {
        apply();
      }
      return result;
    });
  }

  // Remember what the discussion looked like when a *native* reply composer was
  // opened, so hide() can tell a posted reply from a cancel. Captured on show(),
  // not load(): a second reply in the same visit reuses the composer instance
  // (core keeps the body after hide), so load() is not called again.
  if (app.composer && typeof app.composer.show === 'function') {
    const originalShow = app.composer.show;
    app.composer.show = function (...args) {
      const body = this.body;
      if (!nativeReply && !isInlineComposer() && body && body.attrs && body.attrs.discussion && !body.attrs.post) {
        const latest = latestPostIn(body.attrs.discussion);
        nativeReply = {
          discussion: body.attrs.discussion,
          baselinePostId: latest && latest.id ? latest.id() : null
        };
      }
      return originalShow.apply(this, args);
    };
  }

  // A successful composer submit (and the shell's own close) calls
  // app.composer.hide(). The inline host renders inside a Post whose
  // SubtreeRetainer caches it, so the host's onupdate cannot observe the change;
  // hook hide() to clear the inline form and refresh the tree.
  if (app.composer && typeof app.composer.hide === 'function') {
    const originalHide = app.composer.hide;
    app.composer.hide = function (...args) {
      const wasInline = Boolean(inlineReply && inlineReply.mode === 'composer');
      const parentId = wasInline ? inlineReply.postId : null;
      const discussion = wasInline ? inlineReply.discussion : null;
      const baselineId = wasInline ? inlineReply.baselinePostId : null;
      const latest = wasInline ? latestPostIn(discussion) : null;
      // hide() runs for both submit and cancel. A new newest post means a reply
      // was actually posted; otherwise this was a cancel and nothing changed.
      const posted = Boolean(latest && latest.id && String(latest.id()) !== String(baselineId));

      // The same question for a reply made through the native composer. Its post
      // is in the store by now (the save resolved before hide() is called), so
      // comparing the newest post with the baseline identifies it.
      const latestNative = !wasInline && nativeReply ? latestPostIn(nativeReply.discussion) : null;
      const postedNative = Boolean(latestNative && latestNative.id && String(latestNative.id()) !== String(nativeReply ? nativeReply.baselinePostId : null));
      const result = originalHide.apply(this, args);
      if (wasInline) {
        inlineReply = null;
        inlineDraft('');
        composerPreview = false;
        if (posted) {
          revealReply(parentId);
          forceRedraw();
          refreshTree(latest.id());
        } else {
          // Cancelled: clear the form without revealing or scrolling anywhere.
          forceRedraw();
        }
      } else if (nativeReply) {
        const repliedTo = postedNative ? getParentId(latestNative, settings.legacyMentions) : null;
        nativeReply = null;
        if (postedNative) {
          // Same treatment as the inline form: unfold the reply's ancestors,
          // refetch the tree, then scroll the new reply into view and highlight
          // it. Without this the composer's own stream.update()/goToNumber()
          // lands on core's flat stream, which the tree has already replaced.
          revealReply(repliedTo);
          forceRedraw();
          refreshTree(latestNative.id());
        }
      }
      return result;
    };
  }

  // Flarum's discussion-list links resume at the first unread post. Optionally
  // open discussions at the top so readers start with the original post.
  if (settings.startAtFirstPost) {
    override('flarum/forum/components/DiscussionListItem', 'getJumpTo', function (original) {
      // Keep search-result jumps so the matched post is still highlighted.
      if (this.attrs.params && this.attrs.params.q) return original();
      return 1;
    });
  }

  // Capture the deep-link target (`/d/<id>/<near>`) BEFORE core resolves it.
  // Core scrolls the native flat stream to `near`, but our tree then replaces
  // that stream (loadAllPosts -> swap), which reflows the page and drops the
  // viewport off the target. We re-apply the scroll once the tree is rendered.
  override('flarum/forum/components/DiscussionPage', 'oninit', function (original, vnode) {
    const raw = m.route.param('near');
    const parsed = raw && raw !== 'reply' ? parseInt(raw, 10) : NaN;
    deepLinkNear = Number.isInteger(parsed) && parsed > 1 ? parsed : null;
    deepLinkApplied = false;
    scrollReapplied = false;
    loadingScrollDone = false;
    return original(vnode);
  });

  // Flarum's DiscussionPage lets the browser restore the pre-reload scroll
  // (`useBrowserScrollRestoration = true` -> `history.scrollRestoration = 'auto'`),
  // which lands you mid-page over the not-yet-rendered stream and fights our
  // loader/target scrolling. Force manual restoration for discussions so our own
  // scroll is the only movement.
  override('flarum/forum/components/DiscussionPage', 'oncreate', function (original, vnode) {
    const result = original(vnode);
    if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    return result;
  });

  // Open the original post's reply form once when its discussion appears, so a
  // reader can comment from the top — on every screen size. On a phone the
  // pinned `ReplyDock--bar` (itqan-theme) hides while this form is open.
  //
  // `onupdate` rather than `oncreate`: core only sets `this.discussion` after the
  // API response lands, which is past the first render. The guard opens once per
  // discussion visit but never latches a *skipped* attempt (the tree still
  // loading, a composer already open, a leftover form…), so navigating back and
  // forth between discussions keeps working. A manual close stays closed, and a
  // posted comment closes the form through the inline form's own `onSubmitted`.
  extend('flarum/forum/components/DiscussionPage', 'onupdate', function () {
    const discussion = this.discussion;
    if (!discussion) return;
    const id = String(discussion.id());

    // The extension loads every page into `allPosts` before the tree renders,
    // and core's `discussion.firstPost()` is empty on this page, so take the
    // original post from there. Until it arrives, try again on the next update.
    const firstPost = allPosts && allPosts.length ? allPosts.find(post => isOriginalPost(post)) : null;
    const canOpen = canOpenOpComposer({
      isCurrentDiscussion: Boolean(currentDiscussion && String(currentDiscussion.id()) === id),
      hasFirstPost: Boolean(firstPost),
      hasUser: Boolean(app.session.user),
      canReply: !(typeof discussion.canReply === 'function' && !discussion.canReply()),
      composerVisible: app.composer.isVisible(),
      inlineReplyOpen: Boolean(inlineReply)
    });
    if (!opComposerAutoOpen.claim(id, canOpen)) return;
    openInlineReply(firstPost, {
      auto: true
    });

    // The tree may still be mid-render when this update runs, so the post that
    // now holds the inline form could have computed its footer before the form
    // existed. Nudge one more redraw once the frame settles.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => forceRedraw());
    }
  });

  // Vote rail on the discussion list. It votes the discussion's first post — the
  // same model the details page votes — so the two views stay in sync.
  extend('flarum/forum/components/DiscussionListItem', 'contentItems', function (items) {
    if (!settings.showVotes) return;
    const discussion = this.attrs.discussion;
    const firstPost = discussion && typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
    if (!firstPost) return;
    items.add('nestedRepliesVote', m(VoteRail, {
      post: firstPost,
      adapter: votes
    }), 110);
  });

  // DiscussionListItem caches its subtree and only rebuilds on read-state
  // changes, so key it on the first post's vote too. Without this a vote
  // updates the model but the row keeps the old score until a full re-render.
  extend('flarum/forum/components/DiscussionListItem', 'oninit', function () {
    this.subtree.check(() => {
      const discussion = this.attrs.discussion;
      const firstPost = discussion && typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
      return firstPost ? `${firstPost.attribute('votes')}:${firstPost.attribute('userVote')}` : '';
    });
  });

  // Core puts `aria-label` on the deleted-author avatar's plain <span>, which
  // is a prohibited ARIA attribute without a role (axe: aria-prohibited-attr).
  // The label is useful — it names the author and date — so give the span the
  // role that makes it valid instead of dropping the label.
  const labelAvatarRoles = root => {
    root?.querySelectorAll?.('.DiscussionListItem-author-avatar[aria-label]:not([role])').forEach(el => el.setAttribute('role', 'img'));
  };

  // Registered on the imported class rather than by module path: the path
  // form resolves lazily and would miss the rows already on screen.
  //
  // `oncreate` fires before Mithril appends the row's children, so the avatar
  // is not in the tree yet; patch on the next frame, and again on every update.
  extend(DiscussionListItem.prototype, 'oncreate', function () {
    requestAnimationFrame(() => labelAvatarRoles(this.element));
  });
  extend(DiscussionListItem.prototype, 'onupdate', function () {
    labelAvatarRoles(this.element);
  });
  function isLikedByMe(post) {
    if (!app.session.user || typeof post.likes !== 'function') return false;
    const likes = post.likes();
    if (!Array.isArray(likes)) return false;
    return likes.some(user => user === app.session.user || user && typeof user.id === 'function' && String(user.id()) === String(app.session.user.id()));
  }
  function syncLikedClass(element, post) {
    const item = element.querySelector('.item-like');
    if (item) item.classList.toggle('is-liked', isLikedByMe(post));
  }
  function decorate(component) {
    const post = component.attrs.post;
    const element = component.$ ? component.$()[0] : null;
    if (!post || !element) return;
    const id = String(post.id());
    const depth = getDepth(post, settings.maxDepth, lookup, settings.legacyMentions);
    // A reply is hidden when the reader collapsed an ancestor or when a sibling
    // group above it is folded behind a "Show more replies" control.
    const hidden = isHidden(post, collapsed, lookup, settings.legacyMentions) || foldPlan.hidden.has(id);
    const op = isOriginalPost(post);
    element.classList.add('NestedRepliesPost');
    element.classList.toggle('NestedRepliesPost--op', op);
    // Brief highlight on the reply the reader just posted.
    element.classList.toggle('NestedRepliesPost--new', highlightedPostId != null && id === highlightedPostId);

    // Flarum 2.x ships a `.Post-container` wrapper; 1.x has an unnamed div.
    // Tag it ourselves so the LESS works on both.
    const container = element.firstElementChild;
    if (container) container.classList.add('NestedRepliesPost-container');
    element.dataset.depth = String(depth);
    element.style.setProperty('--depth', String(depth));

    // Tag the stream item so the reply card can draw separators between
    // top-level replies only, and never between nested ones.
    const item = element.parentElement;
    if (item && item.classList && item.classList.contains('PostStream-item')) {
      item.classList.toggle('is-top-level', depth === 0 && !op);
      item.classList.toggle('is-nested', depth > 0);
    }

    // The post that anchors a "Show more replies" control sits flush with its
    // fold point, so the indent guides end at the control instead of trailing
    // past it.
    element.classList.toggle('NestedRepliesPost--hasMore', foldPlan.moreAfter.has(id));
    if (collapsed.has(id)) element.dataset.collapsed = 'true';else delete element.dataset.collapsed;
    if (hidden) {
      element.dataset.hidden = 'true';
      // The wrapper draws the separator line via ::after, so hiding only the
      // article leaves a 1px rule behind. Hide the whole stream item.
      if (item) item.dataset.hidden = 'true';
    } else {
      delete element.dataset.hidden;
      if (item) delete item.dataset.hidden;
    }
    syncLikedClass(element, post);

    // The reply target is shown as a tag in the header, so hide only the inline
    // mention that points at the stored parent. A legacy-derived parent is the
    // mention itself, so hide it too even when the tag is off.
    if (settings.showReplyTag || isDerivedParent(post, settings.legacyMentions)) {
      const parentId = getParentId(post, settings.legacyMentions);
      const target = parentId ? getReplyTarget(post, lookup, settings.legacyMentions) : null;
      if (parentId && target && target.name) {
        const body = element.querySelector('.Post-body') || element.querySelector('.Post-content');
        if (body) {
          const mention = body.querySelector(`a.PostMention[data-id="${parentId}"]`);
          if (mention) mention.classList.add('NestedRepliesReplyTag-source');
        }
      }
    }
  }

  // Pull every page of the discussion from the API so sorting sees all posts.
  async function fetchAllPosts() {
    if (!currentDiscussion) return [];
    const filter = {
      discussion: currentDiscussion.id()
    };
    const limit = 50;
    const collected = [];
    let offset = 0;
    let effective = limit;
    for (let guard = 0; guard < 500; guard++) {
      const page = await app.store.find('posts', {
        filter,
        page: {
          offset,
          limit
        },
        sort: 'number'
      });
      if (!page || !page.length) break;
      if (offset === 0) effective = page.length;
      collected.push(...page);
      if (page.length < effective) break;
      offset += page.length;
    }
    return collected;
  }

  // Order the replies as a tree: sort each sibling group by the chosen mode and
  // walk depth-first so children always follow their parent.
  function buildReplyOrder(posts, mode) {
    const byId = new Map();
    posts.forEach(post => byId.set(String(post.id()), post));
    const op = posts.find(post => isOriginalPost(post)) || null;
    const opId = op ? String(op.id()) : null;
    const children = new Map();
    const roots = [];
    posts.forEach(post => {
      if (op && post === op) return;
      const parentId = getParentId(post, settings.legacyMentions);
      if (parentId && parentId !== opId && byId.has(parentId)) {
        const list = children.get(parentId) || [];
        list.push(post);
        children.set(parentId, list);
      } else {
        roots.push(post);
      }
    });
    const counts = new Map();
    const countDescendants = (post, seen) => {
      const id = String(post.id());
      if (seen.has(id)) return 0;
      seen.add(id);
      const kids = children.get(id) || [];
      let total = 0;
      kids.forEach(kid => {
        total += 1 + countDescendants(kid, seen);
      });
      counts.set(id, total);
      return total;
    };
    posts.forEach(post => {
      if (!counts.has(String(post.id()))) countDescendants(post, new Set());
    });
    const time = post => Number(post.createdAt ? post.createdAt() : 0) || 0;
    const score = post => Number(post.attribute ? post.attribute('votes') : 0) || 0;
    const replyCount = post => counts.get(String(post.id())) || 0;
    const comparator = (a, b) => {
      if (mode === 'newest') return time(b) - time(a);
      if (mode === 'top') return score(b) - score(a) || time(a) - time(b);
      if (mode === 'replies') return replyCount(b) - replyCount(a) || time(a) - time(b);
      return time(a) - time(b);
    };
    const ordered = [];
    const walk = list => {
      list.sort(comparator);
      list.forEach(post => {
        ordered.push(post);
        const kids = children.get(String(post.id()));
        if (kids) walk(kids);
      });
    };
    walk(roots);
    return {
      op,
      ordered
    };
  }
  function makePostItem(post, index) {
    const PostComponent = app.postComponents[post.contentType()];
    if (!PostComponent) return null;
    const createdAt = post.createdAt ? post.createdAt() : null;
    return m('div.PostStream-item', {
      key: 'post' + post.id(),
      'data-index': index,
      'data-number': post.number(),
      'data-id': post.id(),
      'data-type': post.contentType(),
      'data-time': createdAt && createdAt.toISOString ? createdAt.toISOString() : undefined
    }, m(PostComponent, {
      post
    }));
  }
  const replySortVNode = discussion => {
    const trans = key => app.translator.trans(`mtareq-nested-replies.forum.${key}`);
    const options = [['oldest', trans('sort_oldest'), 'far fa-clock'], ['top', trans('sort_top'), 'fas fa-fire'], ['newest', trans('sort_newest'), 'fas fa-bolt'], ['replies', trans('sort_replies'), 'far fa-comments']];
    const postIds = discussion && typeof discussion.postIds === 'function' ? discussion.postIds() : [];
    const commentCount = discussion && typeof discussion.commentCount === 'function' ? discussion.commentCount() : postIds.length;
    const count = Math.max(0, commentCount - 1);

    // The stream is a `role="feed"`, whose only permitted children are
    // `article`s. The toolbar (and its radiogroup) is not one, so give the
    // toolbar the role the feed requires; axe otherwise flags the whole feed
    // as aria-required-children.
    return m('div.itqan-stream-toolbar', {
      key: 'nestedRepliesReplySort',
      role: 'article',
      'aria-label': app.translator.trans('mtareq-nested-replies.forum.sort_label')
    }, [m('div.itqan-stream-toolbar-title', [m('i.icon.far.fa-comments'), m('span', app.translator.locale === 'ar' || !app.translator.locale || app.translator.locale.startsWith('ar') ? formatArabicPlural(count, 'comment') : app.translator.trans('mtareq-nested-replies.forum.heading_count', {
      count
    }))]), m('div.itqan-sort-segmented', {
      role: 'radiogroup',
      'aria-label': app.translator.trans('mtareq-nested-replies.forum.sort_label')
    }, options.map(([value, label, iconClass]) => m('button', {
      key: value,
      type: 'button',
      role: 'radio',
      className: 'itqan-sort-segment',
      'aria-checked': sortMode === value ? 'true' : 'false',
      disabled: loadingAll,
      title: typeof label === 'object' ? label.join ? label.join('') : String(label) : String(label),
      onclick: () => setSortMode(value)
    }, [m('i.icon.' + iconClass.replace(/ /g, '.')), m('span.itqan-sort-segment-label', label)]))), loadingAll ? m('span.NestedRepliesReplySort-loading', trans('sort_loading')) : null]);
  };

  // The tree layout needs every reply in the discussion so children can be
  // nested under their parent, so we load all pages once per discussion (for
  // every sort mode, including the default `oldest`).
  function loadAllPosts() {
    if (allPosts !== null || loadingAll) return;
    loadingAll = true;
    m.redraw();
    fetchAllPosts().then(posts => {
      allPosts = posts && posts.length ? posts : [];
    }).catch(() => {
      allPosts = [];
    }).then(() => {
      loadingAll = false;
      m.redraw();
    });
  }
  function setSortMode(mode) {
    sortMode = mode;
    loadAllPosts();
    m.redraw();
  }

  // Core's infinite-scroll pagination anchors on `.PostStream-item[data-index]`
  // elements it expects to own. While our tree renders the whole discussion that
  // pagination must not run: core's anchorScroll throws when its selector finds
  // nothing. Once the tree owns a stream, its loadPage becomes a no-op.
  function guardStreamPagination(stream) {
    if (!stream || stream.__nestedRepliesPaginationGuarded) return;
    if (typeof stream.loadPage !== 'function') return;
    stream.__nestedRepliesPaginationGuarded = true;
    stream.loadPage = function () {};
  }

  // ---------------------------------------------------------------------------
  // Position tracking: URL + read state
  // ---------------------------------------------------------------------------
  // Core's PostStream computes the visible post numbers in `onscroll`, but it
  // returns early while `stream.paused` — and we pause the flat stream as soon
  // as the tree owns the layout. Without this, scrolling never updates the URL
  // and posts are never marked read. Mirror core's emit from our own DOM.
  let lastPosition = {
    start: null,
    end: null
  };
  let positionTimer = null;
  function emitTreePosition(component) {
    const items = treeItems();
    if (!items.length) return;
    const viewportHeight = window.innerHeight;
    let start = null;
    let end = null;
    for (const el of items) {
      const rect = el.getBoundingClientRect();
      if (rect.height === 0) continue;
      if (rect.bottom <= 0) continue;
      if (rect.top >= viewportHeight) break;
      const number = parseInt(el.getAttribute('data-number'), 10);
      if (!number) continue;
      if (start === null) start = number;
      end = number;
    }
    if (start === null) return;
    if (start === lastPosition.start && end === lastPosition.end) return;
    lastPosition = {
      start,
      end
    };
    const onPositionChange = component.attrs && component.attrs.onPositionChange;
    if (typeof onPositionChange === 'function') onPositionChange(start, end, start);
  }
  override('flarum/forum/components/PostStream', 'oncreate', function (original, vnode) {
    const result = original(vnode);
    const handler = () => {
      clearTimeout(positionTimer);
      positionTimer = setTimeout(() => emitTreePosition(this), 120);
    };
    this.__treePositionHandler = handler;
    window.addEventListener('scroll', handler, {
      passive: true
    });

    // Short discussions can be fully on screen without a scroll event. Skip the
    // initial emit when a deep link is still pending, or it would rewrite the
    // URL before our re-scroll lands.
    if (!deepLinkNear) setTimeout(() => emitTreePosition(this), 600);
    return result;
  });
  override('flarum/forum/components/PostStream', 'onremove', function (original, vnode) {
    if (this.__treePositionHandler) {
      window.removeEventListener('scroll', this.__treePositionHandler);
      this.__treePositionHandler = null;
    }
    clearTimeout(positionTimer);
    return original(vnode);
  });

  // Flarum's post stream is a flat list. A nested-reply layout wants the original
  // post in its own card and every reply inside a second card, so regroup the
  // rendered vnodes without touching core.
  override('flarum/forum/components/PostStream', 'view', function (original) {
    const nextDiscussion = this.discussion;
    if (nextDiscussion !== currentDiscussion) {
      currentDiscussion = nextDiscussion;
      collapsed.clear();
      expandedGroups.clear();
      allPosts = null;
      loadingAll = false;
      refreshing = false;
      deepLinkApplied = false;
      scrollReapplied = false;
      loadingScrollDone = false;
      invalidateTreeScrollCache();
      foldPlan = {
        hidden: new Set(),
        moreAfter: new Map()
      };
      // A reply form left open on the previous discussion must not leak into
      // this one: it would render nowhere here and block the auto-open. (The
      // draft itself is cleared by `openInlineReply` whenever a form opens.)
      inlineReply = null;
      composerPreview = false;
      opComposerAutoOpen.reset();
      // New discussion: the previous positions no longer apply.
      lastPosition = {
        start: null,
        end: null
      };
    }

    // Kick off loading every page so the tree can be ordered. The flat native
    // stream renders meanwhile and is swapped out once the posts arrive.
    loadAllPosts();
    const vnode = original();

    // Show a loader until every page has been fetched and the tree can render.
    // Otherwise the native flat stream is rendered (and core scrolls it to the
    // target), then replaced by the tree — the user sees a teleport.
    if (allPosts === null) {
      // On a reload the browser restores the previous (often mid-page) scroll,
      // which leaves this loader above the viewport — an empty screen until the
      // tree arrives. Pull the loader into view once per discussion so the user
      // sees the spinner; the post-swap scroll then takes them to the target.
      if (!loadingScrollDone) {
        loadingScrollDone = true;
        requestAnimationFrame(() => {
          // Stay at the very top while loading (the spinner sits below the hero,
          // and any earlier scroll offset would leave us over empty space).
          window.scrollTo(0, 0);
        });
      }

      // Core marks the stream as `role="feed"`, which only allows `article`
      // children; the loading indicator is `role="status"`. Drop the feed role
      // for the loading paint (there is nothing to feed yet) and keep the
      // busy state so assistive tech still announces work in progress.
      return m('div.PostStream', {
        ...vnode.attrs,
        role: undefined,
        'aria-busy': 'true'
      }, m(LoadingIndicator));
    }
    if (allPosts && allPosts.length) {
      // The layout is about to change; the scrubber's DOM cache is stale.
      invalidateTreeScrollCache();
      // The tree view renders the whole discussion from our own ordering, so
      // stop the native stream from paginating underneath it.
      if (this.stream) {
        this.stream.paused = true;
        guardStreamPagination(this.stream);
      }
      const {
        op,
        ordered
      } = buildReplyOrder(allPosts, sortMode);

      // Deep-link: now that the tree owns the stream, reveal the target post's
      // ancestors and re-arm core's scroll so it lands — and stays — on the
      // target, instead of on wherever the native flat stream happened to be
      // when we swapped it out.
      if (!deepLinkApplied && deepLinkNear != null) {
        const nearPost = allPosts.find(post => Number(post.number()) === deepLinkNear);
        if (nearPost) {
          deepLinkApplied = true;
          const parentId = getParentId(nearPost, settings.legacyMentions);
          if (parentId) revealReply(parentId);
          if (this.stream) {
            this.stream.needsScroll = true;
            this.stream.targetPost = {
              number: deepLinkNear
            };
            this.stream.animateScroll = true;
          }
        }
      }

      // Core's native render consumed `needsScroll` before our tree existed, so
      // re-arm its scroll once to target the tree — covers deep-links handled
      // above, and resume-at-unread. Without this the target is lost on swap.
      if (!scrollReapplied && this.stream && this.stream.targetPost) {
        scrollReapplied = true;
        this.stream.needsScroll = true;
        this.stream.animateScroll = true;
      }
      foldPlan = planSiblingFolding(ordered, {
        lookup,
        visibleReplies: settings.visibleReplies,
        expandedParents: expandedGroups,
        legacyMentions: settings.legacyMentions
      });
      const chrono = [...allPosts].sort((a, b) => Number(a.number()) - Number(b.number()));
      const indexOf = new Map(chrono.map((post, i) => [String(post.id()), i]));
      const opItem = op ? makePostItem(op, indexOf.get(String(op.id())) || 0) : null;
      const replyItems = ordered.map(post => makePostItem(post, indexOf.get(String(post.id())) || 0)).filter(Boolean);
      const grouped = [];
      if (opItem) grouped.push(m('div.NestedRepliesThreadCard', {
        key: 'nestedRepliesThreadCard'
      }, opItem));

      // Skip the reply card entirely when there are no replies: the sort header
      // ("Sort by:") must not render on a discussion with no replies. Mirrors
      // the fallback path below.
      if (replyItems.length) {
        const toolbar = replySortVNode(this.discussion || this.stream && this.stream.discussion);
        if (toolbar) grouped.push(toolbar);
        grouped.push(m('div.NestedRepliesReplyCard', {
          key: 'nestedRepliesReplyCard'
        }, replyItems));
      }
      return m('div.PostStream', vnode.attrs, grouped);
    }
    if (this.stream) this.stream.paused = false;
    const children = vnode && Array.isArray(vnode.children) ? vnode.children : null;
    if (!children || !children.length) return vnode;
    const isPostItem = child => Boolean(child && child.attrs && child.attrs['data-id'] != null);
    const opIndex = children.findIndex(child => child && child.attrs && String(child.attrs['data-number']) === '1');

    // If the original post isn't in the current page (e.g. scrolled into the
    // middle of a long discussion), leave the stream untouched.
    if (opIndex === -1) return vnode;
    const before = children.slice(0, opIndex);
    const op = children[opIndex];
    const rest = children.slice(opIndex + 1);
    const replies = rest.filter(isPostItem);
    const tail = rest.filter(child => !isPostItem(child));
    const replyPosts = replies.map(child => lookup(child.attrs['data-id'])).filter(Boolean);
    foldPlan = planSiblingFolding(replyPosts, {
      lookup,
      visibleReplies: settings.visibleReplies,
      expandedParents: expandedGroups,
      legacyMentions: settings.legacyMentions
    });
    const grouped = [...before, m('div.NestedRepliesThreadCard', {
      key: 'nestedRepliesThreadCard'
    }, op)];
    if (replies.length) {
      const toolbar = replySortVNode(this.discussion || this.stream && this.stream.discussion);
      if (toolbar) grouped.push(toolbar);
      grouped.push(m('div.NestedRepliesReplyCard', {
        key: 'nestedRepliesReplyCard'
      }, replies));
    }
    grouped.push(...tail);
    return m('div.PostStream', vnode.attrs, grouped);
  });

  // Flarum 1.x skips a Post's redraw unless its SubtreeRetainer says a rebuild
  // is needed, so invalidate the mounted posts ourselves before redrawing.
  function forceRedraw() {
    mounted.forEach(post => {
      if (post.subtree && typeof post.subtree.invalidate === 'function') {
        post.subtree.invalidate();
      }
    });
    m.redraw();
  }
  function isInlineComposer() {
    return Boolean(inlineReply && inlineReply.mode === 'composer' && app.composer && app.composer.isVisible() || editingPostId !== null);
  }

  // While the reply form is inline, hide the fixed composer shell and disable
  // the shell's layout side effects (page padding, phone backdrop, resizing).
  override('flarum/forum/components/Composer', 'view', function (original) {
    // Keep the same root tag so Mithril reuses the shell's DOM node (and the
    // lifecycle handlers bound in oncreate) instead of orphaning them.
    if (isInlineComposer()) return m('div.Composer.Composer--nestedInlineHidden');
    return original();
  });
  override('flarum/forum/components/Composer', 'updateBodyPadding', function (original) {
    if (isInlineComposer()) return;
    return original();
  });
  override('flarum/forum/components/Composer', 'animatePositionChange', function (original) {
    if (isInlineComposer()) return;
    return original();
  });
  override('flarum/forum/components/Composer', 'updateHeight', function (original) {
    if (isInlineComposer()) return;
    return original();
  });
  function closeInlineReply() {
    if (inlineReply && inlineReply.mode === 'composer' && app.composer && app.composer.isVisible()) {
      app.composer.close();

      // A cancelled discard confirmation leaves the composer visible; keep the
      // inline host so the draft is not lost.
      if (app.composer.isVisible()) return;
    }
    inlineReply = null;
    inlineDraft('');
    composerPreview = false;
    forceRedraw();
  }

  // Make a freshly posted reply visible: clear any collapsed ancestor and
  // expand every sibling group along the parent chain, so a reply that would
  // otherwise be folded behind "Show more replies" is shown.
  function revealReply(parentId) {
    let id = parentId ? String(parentId) : null;
    const seen = new Set();
    while (id && !seen.has(id)) {
      seen.add(id);
      collapsed.delete(id);
      expandedGroups.add(id);
      const post = lookup(id);
      id = post ? getParentId(post, settings.legacyMentions) : null;
    }
  }

  // The newest loaded post in a discussion. Used to focus a reply just posted
  // through the native composer, whose id we never receive.
  function latestPostIn(discussion) {
    if (!discussion || !app.store || typeof app.store.all !== 'function') return null;
    const discussionId = String(discussion.id());
    const posts = app.store.all('posts').filter(post => {
      if (!post || typeof post.number !== 'function') return false;
      const related = post.discussion && post.discussion();
      if (related) return String(related.id()) === discussionId;
      const attr = post.attribute ? post.attribute('discussionId') : null;
      return attr != null && String(attr) === discussionId;
    });
    if (!posts.length) return null;
    return posts.reduce((latest, post) => Number(post.number()) >= Number(latest.number()) ? post : latest);
  }

  // Refetch every page without dropping the current tree, so the view never
  // flashes back to the native fallback. Optionally scroll a just-posted reply
  // into view and briefly highlight it.
  function refreshTree(focusPostId) {
    if (refreshing) return;
    refreshing = true;
    fetchAllPosts().then(posts => {
      if (posts && posts.length) allPosts = posts;
    }).catch(() => {}).then(() => {
      refreshing = false;
      if (focusPostId != null) {
        highlightedPostId = String(focusPostId);
        if (highlightTimer) clearTimeout(highlightTimer);
        highlightTimer = setTimeout(() => {
          highlightedPostId = null;
          forceRedraw();
        }, HIGHLIGHT_DURATION);
      }
      forceRedraw();
      if (focusPostId != null) {
        requestAnimationFrame(() => {
          const element = document.querySelector(`.PostStream-item[data-id="${focusPostId}"]`);
          if (element && element.scrollIntoView) element.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        });
      }
    });
  }
  function openInlineReply(post, options = {}) {
    const id = String(post.id());
    if (inlineReply && inlineReply.postId !== id && String(inlineDraft() || '').trim()) {
      app.modal.show(DeleteConfirmModal, {
        title: app.translator.trans('mtareq-nested-replies.forum.reply_form_discard_title'),
        message: app.translator.trans('mtareq-nested-replies.forum.reply_form_discard'),
        confirmLabel: app.translator.trans('mtareq-nested-replies.forum.reply_form_discard_confirm'),
        cancelLabel: app.translator.trans('mtareq-nested-replies.forum.reply_form_cancel'),
        onconfirm: () => {
          inlineDraft('');
          openInlineReply(post, options);
        }
      });
      return;
    }
    const discussion = post.discussion();
    if (settings.replyForm === 'composer') {
      const previous = inlineReply;
      const baseline = latestPostIn(discussion);
      inlineReply = {
        postId: id,
        discussion,
        mode: 'composer',
        // Newest post id when the form opened; hide() compares against it to
        // tell a real submit apart from a cancel.
        baselinePostId: baseline && baseline.id ? String(baseline.id()) : null
      };
      composerPreview = false;
      pendingParentId = id;
      originalReplyAction.call(discussion);
      const body = app.composer && app.composer.body;
      const isReply = body && body.attrs && body.attrs.discussion && !body.attrs.post;
      const sameDiscussion = isReply && body.attrs.discussion === discussion;

      // An unrelated composer is open (e.g. editing a post), or a reply composer
      // for another discussion: do not hijack it.
      if (app.composer && app.composer.isVisible() && !sameDiscussion) {
        inlineReply = previous;
        pendingParentId = null;
        forceRedraw();
        return;
      }
      if (sameDiscussion) {
        // Already open for this discussion: replyAction skipped the load (and our
        // load override), so retarget the parent directly.
        body.attrs.replyToPostId = id;
        pendingParentId = null;
      }
      // Otherwise the load is in flight; the app.composer.load override applies
      // pendingParentId when the body arrives.

      forceRedraw();
      return;
    }
    inlineReply = {
      postId: id,
      discussion,
      mode: 'quick',
      auto: Boolean(options.auto)
    };
    inlineDraft('');
    forceRedraw();

    // Scroll the reply form into view after it renders — but not for the
    // automatic open on page load. Centring the form drags the reader into the
    // middle of a long original post (and can race a deep-link's own scroll),
    // when the discussion should be shown from the top.
    if (options.auto) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetEl = document.querySelector(`.PostStream-item[data-id="${id}"] .NestedRepliesInlineReply`) || document.querySelector(`.PostStream-item[data-id="${id}"] .item-nestedRepliesInlineReply`) || document.querySelector(`.PostStream-item[data-id="${id}"] .Post-footer`) || document.querySelector(`.PostStream-item[data-id="${id}"]`);
        if (targetEl && targetEl.scrollIntoView) {
          targetEl.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        }
      });
    });
  }
  function editPost(post) {
    if (!post) return;

    // Close any open reply form first. closeInlineReply() uses composer.close(),
    // so an existing draft is confirmed before it is discarded.
    if (inlineReply) closeInlineReply();
    const content = typeof post.content === 'function' ? post.content() : '';
    editingPostId = String(post.id());
    editDraft(content || '');
    forceRedraw();

    // Scroll the post into view so the edit form is visible.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetEl = document.querySelector(`.PostStream-item[data-id="${editingPostId}"] .NestedRepliesInlineEdit`) || document.querySelector(`.PostStream-item[data-id="${editingPostId}"] .item-nestedRepliesInlineEdit`) || document.querySelector(`.PostStream-item[data-id="${editingPostId}"] .Post-footer`) || document.querySelector(`.PostStream-item[data-id="${editingPostId}"]`);
        if (targetEl && targetEl.scrollIntoView) {
          targetEl.scrollIntoView({
            block: 'center',
            behavior: 'smooth'
          });
        }
      });
    });
  }
  function cancelEdit() {
    editingPostId = null;
    editDraft('');
    forceRedraw();
  }
  function toggleCollapse(post) {
    const id = String(post.id());
    if (collapsed.has(id)) collapsed.delete(id);else collapsed.add(id);
    forceRedraw();
  }

  // Reveal every reply hidden behind a folded sibling group's control.
  function expandGroup(parentId) {
    expandedGroups.add(String(parentId));
    forceRedraw();
  }

  // Core renders the hidden-post reveal control as an icon-only button with no
  // accessible name (axe: button-name). Give it a label in both locales.
  const labelPostToggle = root => {
    root?.querySelectorAll?.('.Post-header .item-toggle button.Button--more:not([aria-label])').forEach(el => {
      el.setAttribute('aria-label', app.translator.trans('mtareq-nested-replies.forum.post_toggle_accessible_label'));
    });
  };
  extend('flarum/forum/components/Post', 'oncreate', function () {
    mounted.add(this);
    decorate(this);
    requestAnimationFrame(() => labelPostToggle(this.element));

    // Keep the liked colour in sync even when the post doesn't rebuild.
    if (this.element) {
      this.element.addEventListener('click', e => {
        if (e.target && e.target.closest && e.target.closest('.item-like')) {
          requestAnimationFrame(() => {
            if (this.element) syncLikedClass(this.element, this.attrs.post);
          });
        }
      });
    }
  });
  extend('flarum/forum/components/Post', 'onupdate', function () {
    decorate(this);
    labelPostToggle(this.element);
  });
  extend('flarum/forum/components/Post', 'onremove', function () {
    mounted.delete(this);
  });

  // Surface who a reply answers as a tag in the header, instead of the inline
  // mention Flarum renders at the start of the body.
  extend('flarum/forum/components/CommentPost', 'headerItems', function (items) {
    if (!settings.showReplyTag) return;
    const post = this.attrs.post;
    const target = getReplyTarget(post, lookup, settings.legacyMentions);
    if (!target || !target.name) return;
    items.add('nestedRepliesReplyTag', m(Link, {
      className: 'NestedRepliesReplyTag',
      href: target.post ? app.route.post(target.post) : '#',
      title: target.name,
      onclick: e => {
        if (target.post) {
          const targetEl = document.querySelector(`.PostStream-item[data-id="${target.post.id()}"]`) || document.querySelector(`[data-id="${target.post.id()}"]`);
          if (targetEl) {
            e.preventDefault();
            targetEl.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            targetEl.classList.remove('pulsate');
            void targetEl.offsetWidth;
            targetEl.classList.add('pulsate');
            setTimeout(() => targetEl.classList.remove('pulsate'), 2000);
          }
        }
      }
    }, [icon('fas fa-reply'), m('span.NestedRepliesReplyTag-label', app.translator.trans('mtareq-nested-replies.forum.reply_to', {
      username: target.name
    }))]), 95);
  });

  // Reddit-style controls: a circular fold/unfold button on every comment, and
  // an "N more replies" line underneath a folded comment.
  // CommentPost defines its own actionItems() and does not call the base
  // Post.actionItems(), so this must target CommentPost.
  extend('flarum/forum/components/CommentPost', 'actionItems', function (items) {
    const post = this.attrs.post;
    if (!post) return;

    // Skip vote rail and collapse toggle on deleted / hidden posts — they
    // should not be interactive and the card layout breaks around them.
    if (post.isHidden && post.isHidden()) return;
    if (post.isDeleted && post.isDeleted()) return;

    // Only offer collapse when the reply actually has replies. The backend
    // serializes the subtree count, so this is accurate even before every page
    // of nested replies has loaded.
    const replyCount = Number(post.attribute ? post.attribute('nestedRepliesReplyCount') : 0);
    if (!isOriginalPost(post) && replyCount > 0) {
      items.add('nestedRepliesCollapse', m(CollapseToggle, {
        collapsed: collapsed.has(String(post.id())),
        onclick: () => toggleCollapse(post)
      }), 11);
    }
    if (settings.showVotes) {
      items.add('nestedRepliesVotes', m(VoteRail, {
        post,
        adapter: votes
      }), 10);
    }
  });

  // Remove Like and Reply actions on deleted posts — they should not be
  // interactive. This runs after core and extensions add their items.
  extend('flarum/forum/components/CommentPost', 'actionItems', function (items) {
    const post = this.attrs.post;
    if (!post) return;
    if (!post.isDeleted || !post.isDeleted()) return;
    items.delete('like');
    items.delete('reply');
  });
  extend('flarum/forum/components/CommentPost', 'footerItems', function (items) {
    const post = this.attrs.post;
    if (!post) return;
    const id = String(post.id());

    // Folded sibling groups anchor their "Show more replies" control to the
    // last visible reply of the kept branch. A group can be nested inside
    // another group's kept branch, so render deepest-first.
    const groups = foldPlan.moreAfter.get(id);
    if (groups && groups.length) {
      const actualDepth = getDepth(post, settings.maxDepth, lookup, settings.legacyMentions);
      [...groups].sort((a, b) => b.targetDepth - a.targetDepth).forEach((group, index) => {
        items.add('nestedRepliesShowMore' + index, m(MoreReplies, {
          count: group.count,
          depth: group.targetDepth,
          // Line the control up with the depth of the hidden replies. Hidden
          // replies always belong one level below a post in the anchor's own
          // branch, so the control may sit beside or to the right of the
          // anchor's content column.
          indent: group.targetDepth - actualDepth,
          onclick: () => expandGroup(group.parentId)
        }), 20);
      });
    }
    if (inlineReply && inlineReply.postId === id) {
      const depth = getDepth(post, settings.maxDepth, lookup, settings.legacyMentions);
      const childDepth = Math.min(depth + 1, settings.maxDepth);
      items.add('nestedRepliesInlineReply', m(NestedRepliesInlineReply, {
        post,
        discussion: post.discussion(),
        mode: inlineReply.mode,
        indent: childDepth - depth,
        draft: inlineDraft,
        preview: composerPreview,
        autoFocus: !inlineReply.auto,
        onRedraw: forceRedraw,
        onCancel: closeInlineReply,
        onSubmitted: created => {
          const parentId = inlineReply ? inlineReply.postId : null;
          closeInlineReply();
          revealReply(parentId);
          refreshTree(created && created.id ? created.id() : null);
        },
        onClosed: () => {
          closeInlineReply();
          refreshTree();
        }
      }), 5);
    }

    // Inline edit form — rendered in the same position as the reply form but
    // for editing an existing post.
    if (editingPostId === id) {
      const depth = getDepth(post, settings.maxDepth, lookup, settings.legacyMentions);
      const childDepth = Math.min(depth + 1, settings.maxDepth);
      items.add('nestedRepliesInlineEdit', m(NestedRepliesInlineReply, {
        post,
        discussion: post.discussion(),
        mode: 'quick',
        indent: childDepth - depth,
        draft: editDraft,
        editMode: true,
        onRedraw: forceRedraw,
        onCancel: cancelEdit,
        onSubmitted: () => {
          cancelEdit();
          refreshTree(post.id());
        }
      }), 5);
    }
    if (collapsed.has(id)) {
      const count = Number(post.attribute ? post.attribute('nestedRepliesReplyCount') : 0);
      if (count <= 0) return;
      items.add('nestedRepliesMoreReplies', m(Button, {
        className: 'Button Button--link NestedRepliesMoreReplies',
        onclick: () => toggleCollapse(post)
      }, [icon('fas fa-plus'), m('span', app.translator.trans('mtareq-nested-replies.forum.more_replies', {
        count
      }))]), 10);
    }
  });
});